/**
 * Label/value sections — the legacy `FieldRow` grid and `PartyBlock`.
 *
 * Every value printed here is a slot value. A party section's slots carry
 * party autofill bindings, so Regta's editor fills them from the linked
 * party (auto by default, switchable to manual text) while an API consumer
 * just passes the strings.
 */
import { details, fieldLabel, nodes, p, section, t } from '@hyranai/regta-document-ir';
import { str } from '../lib.js';
import { isFilled, partySlot, shown, textVal } from './slots.js';
/**
 * The legacy `FieldRow` grid (40 % label | 60 % value). Falsy rows drop out,
 * so conditional rows read as `cond && [label, value]`.
 */
export function fieldRows(rows, opts = {}) {
    const present = rows.filter((r) => Boolean(r));
    if (present.length === 0)
        return null;
    return details(present.map(([label, value]) => [label, value]), {
        ...(opts.variant ? { variant: opts.variant } : {}),
        ...(opts.columns && opts.columns > 1 ? { columns: opts.columns } : {}),
    });
}
/**
 * The legacy reference line above the preamble — "AVTALSNUMMER: ____
 * OBJEKTSNUMMER: ____" — with each label printed as a run-in field label.
 * Falsy entries drop out.
 */
export function referenceLine(entries) {
    const present = entries.filter((e) => Boolean(e));
    if (present.length === 0)
        return null;
    const content = present.flatMap(([label, value], i) => [
        ...(i > 0 ? [t('\u2003\u2003')] : []),
        fieldLabel(label.endsWith(':') ? label : `${label}:`),
        t(' '),
        value,
    ]);
    return p(content, { variant: 'fine_print' });
}
/** Resolves `{ ref, title, number, numbering }` from the instance input over the spec's defaults. */
export function sectionPlacement(input, defaults) {
    const number = str(input.number);
    const numbering = input.numbering === 'none' || input.numbering === 'auto' ? input.numbering : (defaults.numbering ?? 'auto');
    return {
        ref: str(input.ref) ?? defaults.ref,
        title: str(input.title) ?? defaults.title,
        opts: {
            ...(defaults.keepTogether === false ? {} : { keepTogether: true }),
            ...(input.beside_previous === true ? { sideBySide: true } : {}),
            ...(number ? { numbering: 'fixed', number } : numbering === 'none' ? { numbering: 'none' } : {}),
        },
    };
}
/**
 * A section of label/value rows where every value is a slot. Placement
 * (`ref`, `title`, a fixed `number`, `numbering: 'none'`) may be overridden
 * per instance.
 */
export function fieldsSection(spec) {
    return {
        blockKey: spec.blockKey,
        version: spec.version ?? 'v1',
        plane: spec.plane ?? 'shared',
        status: 'published',
        language: 'sv-SE',
        title: spec.title,
        summary: spec.summary,
        ...(spec.legalReview ? { legalReview: spec.legalReview } : {}),
        slots: [...spec.rows.map((r) => r.slot), ...(spec.extraSlots ?? [])],
        compile: (input, ctx) => {
            const { ref, title, opts } = sectionPlacement(input, {
                ref: spec.ref,
                title: spec.title,
                ...(spec.numbering ? { numbering: spec.numbering } : {}),
            });
            const rows = fieldRows(spec.rows.map((r) => shown(ctx, r.when ? r.when(input) : true) &&
                [r.printed ?? r.slot.label, [textVal(input, r.slot.key, r.blankWidth ?? 'medium')]]), {
                ...(spec.variant ? { variant: spec.variant } : {}),
                ...(spec.columns ? { columns: spec.columns } : {}),
            });
            const out = [
                section(ref, title, nodes(...(spec.before?.(input, ctx) ?? []), rows, ...(spec.after?.(input, ctx) ?? [])), opts),
            ];
            const attachments = spec.attachments?.(input, ctx) ?? [];
            return attachments.length ? { nodes: out, attachments } : out;
        },
    };
}
const groupsOf = (spec) => 'groups' in spec ? spec.groups : [{ role: spec.role, ...(spec.ordinal != null ? { ordinal: spec.ordinal } : {}), fields: spec.fields }];
/** The slots of a party group, each bound to its party attribute. */
export function partyGroupSlots(group) {
    return group.fields.map((f) => partySlot(f.key, f.label, group.role, group.ordinal ?? 0, f.field, {
        ...(f.type ? { type: f.type } : {}),
        ...(f.required ? { required: true } : {}),
        ...(f.help ? { help: f.help } : {}),
        ...(f.slot ?? {}),
    }));
}
/** One party group's printed rows (subheading + label/value grid), or nothing when gated out. */
export function partyGroupNodes(group, input, ctx, opts = {}) {
    if (group.showWhenAnyOf && !shown(ctx, group.showWhenAnyOf.some((k) => isFilled(input[k]))))
        return [];
    return nodes(group.subheading ? p(group.subheading, { variant: 'caption' }) : null, fieldRows(group.fields.map((f) => [f.printed ?? f.label, [textVal(input, f.key)]]), opts));
}
/**
 * A party section ("Hyresvärd", "Hyresgäst" + optional medhyresgäst) whose
 * rows are slot values bound to `(role, ordinal)` parties by autofill —
 * the legacy `PartyBlock`. Replaces `common.party-panel`, which formatted
 * `ctx.parties` itself.
 */
export function partyDetailsBlock(spec) {
    const groups = groupsOf(spec);
    return {
        blockKey: spec.blockKey,
        version: spec.version ?? 'v1',
        plane: 'shared',
        status: 'published',
        language: 'sv-SE',
        title: spec.title,
        summary: spec.summary,
        ...(spec.legalReview ? { legalReview: spec.legalReview } : {}),
        slots: groups.flatMap(partyGroupSlots),
        compile: (input, ctx) => {
            const body = groups.flatMap((g) => partyGroupNodes(g, input, ctx, {
                ...(spec.variant ? { variant: spec.variant } : {}),
                ...(spec.columns ? { columns: spec.columns } : {}),
            }));
            if (body.length === 0)
                return null;
            if (spec.asSection === false)
                return body;
            const { ref, title, opts } = sectionPlacement(input, {
                ref: spec.ref,
                title: spec.title,
                ...(spec.numbering ? { numbering: spec.numbering } : {}),
            });
            return [section(ref, title, body, opts)];
        },
    };
}
//# sourceMappingURL=fields.js.map